import AsyncStorage from '@react-native-async-storage/async-storage';

const webOrigin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
const isLocalWeb = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(webOrigin);
const host = process.env.EXPO_PUBLIC_API_URL || (webOrigin ? (isLocalWeb ? 'http://localhost:4000' : webOrigin) : 'http://10.0.2.2:4000');
const API_BASE = host.replace(/\/$/, '');
const TOKEN_KEY = 'finance_studio_token';
let authToken = '';

export const setAuthToken = (token) => {
  authToken = token || '';
};

export const loadStoredToken = async () => {
  const token = await AsyncStorage.getItem(TOKEN_KEY);
  setAuthToken(token);
  return token;
};

export const storeAuthSession = async (session) => {
  setAuthToken(session?.token || '');
  if (session?.token) await AsyncStorage.setItem(TOKEN_KEY, session.token);
  return session;
};

export const clearAuthSession = async () => {
  setAuthToken('');
  await AsyncStorage.removeItem(TOKEN_KEY);
};

const request = async (path, options = {}) => {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}), ...(options.headers || {}) },
    ...options,
  });

  if (!response.ok) {
    let message = 'Something went wrong.';
    try {
      const body = await response.json();
      message = body.error || message;
    } catch (error) {
      message = response.statusText || message;
    }
    throw new Error(message);
  }

  if (response.status === 204) return null;
  return response.json();
};

export const login = (credentials) => request('/api/auth/login', {
  method: 'POST',
  body: JSON.stringify(credentials),
}).then(storeAuthSession);

export const register = (payload) => request('/api/auth/register', {
  method: 'POST',
  body: JSON.stringify(payload),
}).then(storeAuthSession);

export const fetchMe = () => request('/api/auth/me');

export const logout = () => request('/api/auth/logout', { method: 'POST' }).finally(clearAuthSession);

const queryString = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.append(key, value);
  });
  const output = search.toString();
  return output ? `?${output}` : '';
};

export const fetchTransactions = (filters) => request(`/api/transactions${queryString(filters)}`);
export const fetchTransactionsPage = (filters) => request(`/api/transactions${queryString(filters)}`);
export const fetchAnalytics = (filters) => request(`/api/analytics${queryString(filters)}`);
export const fetchCategories = () => request('/api/categories');
export const fetchExpenseTemplates = () => request('/api/expense-templates');
export const postExpenseTemplate = (template) => request('/api/expense-templates', {
  method: 'POST',
  body: JSON.stringify(template),
});
export const fetchCutSuggestions = () => request('/api/cut-suggestions');
export const postCutSuggestionGroup = (group) => request('/api/cut-suggestion-groups', {
  method: 'POST',
  body: JSON.stringify(group),
});
export const postCutSuggestion = (suggestion) => request('/api/cut-suggestions', {
  method: 'POST',
  body: JSON.stringify(suggestion),
});
export const postSavingsPlan = (plan) => request('/api/savings-plans', {
  method: 'POST',
  body: JSON.stringify(plan),
});
export const updateSavingsPlan = (id, patch) => request(`/api/savings-plans/${id}`, {
  method: 'PATCH',
  body: JSON.stringify(patch),
});
export const postSavingsRecord = (record) => request('/api/savings-records', {
  method: 'POST',
  body: JSON.stringify(record),
});
export const postRecurringExpense = (expense) => request('/api/recurring-expenses', {
  method: 'POST',
  body: JSON.stringify(expense),
});
export const updateRecurringExpense = (id, patch) => request(`/api/recurring-expenses/${id}`, {
  method: 'PATCH',
  body: JSON.stringify(patch),
});
export const postDailyExpenseLimit = (payload) => request('/api/daily-expense-limit', {
  method: 'POST',
  body: JSON.stringify(payload),
});
export const fetchFundPartitions = (filters) => request(`/api/fund-partitions${queryString(filters)}`);
export const postFundPartition = (partition) => request('/api/fund-partitions', {
  method: 'POST',
  body: JSON.stringify(partition),
});
export const fetchExpensePlans = (filters) => request(`/api/expense-plans${queryString(filters)}`);
export const postExpensePlan = (plan) => request('/api/expense-plans', {
  method: 'POST',
  body: JSON.stringify(plan),
});
export const updateExpensePlan = (id, patch) => request(`/api/expense-plans/${id}`, {
  method: 'PATCH',
  body: JSON.stringify(patch),
});
export const deleteExpensePlan = (id) => request(`/api/expense-plans/${id}`, { method: 'DELETE' });
export const markExpensePlanDone = (id, status = 'done') => request(`/api/expense-plans/${id}/done`, {
  method: 'POST',
  body: JSON.stringify({ status }),
});
export const recordRecurringExpense = (id, payload = {}) => request(`/api/recurring-expenses/${id}/record`, {
  method: 'POST',
  body: JSON.stringify(payload),
});
export const postTransaction = (transaction) => request('/api/transactions', {
  method: 'POST',
  body: JSON.stringify(transaction),
});
export const deleteTransaction = (id) => request(`/api/transactions/${id}`, { method: 'DELETE' });
export const apiBase = API_BASE;
